import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import cn from 'classnames';
import { DateEmpty } from '@picsio/icons';
import { useSelector } from 'react-redux';

import localization from '../../../shared/strings';
import Logger from '../../../services/Logger';
import { TORIUT_TRIAL_EVENTS } from '../../ToriutTrialFlow/analytics';
import { CALENDLY_CONTEXT, CONTACT_DEMO_CALENDLY_PATH } from '../../ToriutTrialFlow/constants';
import getTrialDemoControlState, {
  COPY_KEY,
  DISPLAY_STATE,
  getMsUntilNextZonedDay,
} from './helpers/getTrialDemoControlState';
import s from './styles.module.scss';

const SALES_CALENDLY_HREF = `${CONTACT_DEMO_CALENDLY_PATH}?context=${CALENDLY_CONTEXT.SALES}&utm=toriut_app_trial_control`;

const TrialDemoControl = () => {
  const user = useSelector((state) => state.user);
  const [now, setNow] = useState(() => new Date());
  const copy = localization.TOOLBARS.TRIAL_DEMO_CONTROL;
  const userZone = user?.settings?.timeZone?.userTimeZone;
  const {
    isVisible,
    copyKey,
    displayState,
    daysRemaining,
    trialEndAt,
    promotionalOffer,
  } = useMemo(() => getTrialDemoControlState(user, now), [now, user]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setNow(new Date());
    }, getMsUntilNextZonedDay(now, userZone));

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [now, userZone]);

  const handleBookDemoClick = useCallback(() => {
    Logger.log('User', TORIUT_TRIAL_EVENTS.BOOK_DEMO_CLICKED, {
      entry_point: 'app_trial_control',
      days_remaining: daysRemaining,
      trial_end_at: trialEndAt,
      trial_display_state: displayState,
      promotional_offer: promotionalOffer || '',
    });
  }, [daysRemaining, displayState, promotionalOffer, trialEndAt]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={s.root} data-testid="toriutAppTrialControl">
      <div className={s.copy}>
        <span
          className={cn(s.countdown, {
            [s.warning]: displayState !== DISPLAY_STATE.DEFAULT,
          })}
        >
          <Choose>
            <When condition={copyKey === COPY_KEY.ENDS_TODAY}>
              {copy.endsToday}
            </When>
            <When condition={copyKey === COPY_KEY.ENDS_TOMORROW}>
              {copy.endsTomorrow}
            </When>
            <Otherwise>
              {copy.endsInDays(daysRemaining)}
            </Otherwise>
          </Choose>
        </span>
        <If condition={Boolean(promotionalOffer)}>
          <span className={s.promo}>{` · ${promotionalOffer}`}</span>
        </If>
      </div>
      <a
        className={s.cta}
        href={SALES_CALENDLY_HREF}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="toriutAppTrialControlBookDemo"
        onClick={handleBookDemoClick}
      >
        <DateEmpty />
        <span>{copy.bookDemo}</span>
      </a>
    </div>
  );
};

export default TrialDemoControl;
